import { useState } from 'react';

import { RestoreOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummaryProps,
  Button,
  Grid,
  Typography,
  styled,
} from '@mui/material';
import MuiAccordionSummary from '@mui/material/AccordionSummary';

import { IIPENSessionData } from '../../../types/IPENSessionData';
import { DocGenerationSource } from '../../constants/docGenerationSource';
import { OrderTypes } from '../../constants/orderTypeConstants';
import { settlementTeamColumns } from '../../utils/OrderDetailsColumns';
import {
  goGetDocuments,
  goGetDocumentsType,
} from '../../utils/documentsDownload';
import { isOrderInactive } from '../../utils/helpers';
import { Status } from '../../utils/notaryStatus';
import ConformXNotaryDetails from '../ConformXNotaryDetails';
import ConformXPartyInfo from '../ConformXPartyInfo';
import DocumentsDownloaderUploader from '../DocumentDownloaderUploader';
import OrderDetailsAccordionCard from '../OrderDetailsAccordionCard';
import OrderDetailsTable from '../Tables/OrderDetailsTable';

export const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ExpandMoreIcon />} {...props} />
))(({ theme }) => ({
  color: theme.palette.primary.main,
  borderTop: '6px solid',
  borderColor: theme.palette.primary.main,
  borderTopLeftRadius: 3,
  borderTopRightRadius: 3,
  '&.Mui-expanded': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    height: '40px',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    color: theme.palette.primary.contrastText,
  },
}));

interface IProps {
  order: any;
  orderDetailsPanel: boolean;
  partyPanel: boolean;
  settlementPanel: boolean;
  notaryPanel?: boolean;
  downloadPanel: boolean;
  orderDetailsRef: any;
  documentRef: any;
  params: any;
  isCurrentUserEscrowOnOrder: boolean;
  documentZIndex: number;
  orderZIndex: number;
  ipenSession?: any;
  ipenSessionData?: IIPENSessionData | null;
  setOrderDetailsPanel: (isOpen: boolean) => void;
  goPrintAllInkSignDocuments?: () => void;
  setPartyPanel: (isOpen: boolean) => void;
  setNotaryPanel?: (isOpen: boolean) => void;
  setSettlementPanel: (isOpen: boolean) => void;
  setDownloadPanel: (isOpen: boolean) => void;
  setOpenDialog: (v: boolean) => void;
  setOpenSettlementHistory?: (v: boolean) => void;
  goResetLogin: (partyId: string) => void;
  goUnlockSession?: (partyId: string) => void;
  getSsoToken: (arg: string) => any;
}

function OrderDetailsAccordion(props: IProps) {
  const {
    order,
    orderDetailsPanel,
    partyPanel,
    settlementPanel,
    notaryPanel,
    downloadPanel,
    orderDetailsRef,
    documentRef,
    params,
    isCurrentUserEscrowOnOrder,
    orderZIndex,
    documentZIndex,
    ipenSession,
    ipenSessionData,
    setOrderDetailsPanel,
    goPrintAllInkSignDocuments,
    setPartyPanel,
    setNotaryPanel,
    setSettlementPanel,
    setOpenDialog,
    setOpenSettlementHistory,
    setDownloadPanel,
    goResetLogin,
    goUnlockSession,
    getSsoToken,
  } = props;

  const [documentsSelected, setDocumentsSelected] = useState<any>([]);

  const getSettlementTeamTableData = () => {
    if (order?.OrderType?.DocGen === DocGenerationSource.CX) {
      const saTeam = order?.OrderDetails?.SettlementTeam;
      const mappedSaTeam: any = [];
      saTeam.forEach((sa: { Type: string; PhoneNumbers: any[] }) => {
        mappedSaTeam.push({
          ...sa,
          PhoneNumbers: sa.PhoneNumbers[0]?.Number
            ? sa.PhoneNumbers
            : [{ CountryCode: '-' }],
          Type:
            sa.Type === 'SettlementAgent'
              ? 'Settlement Agent'
              : 'Co-Settlement Agent',
        });
      });

      return mappedSaTeam;
    } else {
      const saTeam = order?.OrderDetails?.EscrowTeam;
      const mappedEsrowTeam: any = [];
      saTeam.forEach((sa: { Type: string; PhoneNumbers: any[] }) => {
        mappedEsrowTeam.push({
          ...sa,
          PhoneNumbers: sa.PhoneNumbers[0]?.Number
            ? sa.PhoneNumbers
            : [{ CountryCode: '-' }],
        });
      });

      return mappedEsrowTeam;
    }
  };

  return (
    <>
      <Grid
        container
        spacing={0}
        alignItems='center'
        justifyContent='center'
        style={{
          marginTop: 35,
          marginBottom: 100,
          paddingLeft: 0,
          paddingRight: 0,
        }}>
        <Grid item xs={11} sm={11} md={11} lg={10}>
          <Accordion
            expanded={orderDetailsPanel}
            ref={orderDetailsRef}
            onChange={() => setOrderDetailsPanel(!orderDetailsPanel)}
            sx={{ marginTop: 1, marginBottom: 1, zIndex: orderZIndex }}>
            <AccordionSummary
              aria-controls='orderDetails-content'
              id='orderDetails'>
              <Typography>ORDER DETAILS</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                className={isOrderInactive(order?.Status) ? 'cancel-style' : ''}
                item>
                {order && order.OrderDetails && (
                  <OrderDetailsAccordionCard
                    order={{ ...order, id: params.id }}
                  />
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={partyPanel}
            onChange={() => setPartyPanel(!partyPanel)}
            sx={{ marginTop: 1, marginBottom: 1, zIndex: orderZIndex }}>
            <AccordionSummary aria-controls='party-content' id='party'>
              <Grid container alignItems={'center'}>
                <Grid item xs={partyPanel ? 5.8 : 12}>
                  <Typography>PARTY INFORMATION</Typography>
                </Grid>
                {partyPanel && (
                  <Grid
                    item
                    xs={6}
                    sx={{ justifyContent: 'flex-end', display: 'flex' }}>
                    <Button
                      size='small'
                      sx={{ marginBottom: '3px', color: 'white' }}
                      startIcon={<RestoreOutlined />}
                      onClick={() => setOpenDialog(true)}>
                      View History
                    </Button>
                  </Grid>
                )}
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                className={isOrderInactive(order?.Status) ? 'cancel-style' : ''}
                item>
                {order.OrderType.Type === OrderTypes.IPEN && (
                  <ConformXPartyInfo
                    unlockParty={goResetLogin}
                    unlockSession={goUnlockSession}
                    data={order}
                    ipenSession={ipenSession}
                    userType={ipenSessionData?.userType}
                    isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
                  />
                )}
                {order.OrderType.Type === OrderTypes.HybridEClose && (
                  <ConformXPartyInfo
                    unlockParty={goResetLogin}
                    data={order}
                    ipenSession={ipenSession}
                    userType={['escrow']}
                    isCurrentUserEscrowOnOrder={isCurrentUserEscrowOnOrder}
                  />
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={settlementPanel}
            onChange={() => setSettlementPanel(!settlementPanel)}
            sx={{ marginTop: 1, marginBottom: 1 }}>
            <AccordionSummary
              aria-controls='settlement-content'
              id='settlement'>
              <Grid container alignItems={'center'}>
                <Grid item xs={settlementPanel ? 5.8 : 12}>
                  <Typography>
                    {order?.OrderType?.DocGen === DocGenerationSource.CX
                      ? 'SETTLEMENT TEAM'
                      : 'ESCROW TEAM'}
                  </Typography>
                </Grid>
                {settlementPanel && (
                  <Grid
                    item
                    xs={6}
                    sx={{ justifyContent: 'flex-end', display: 'flex' }}>
                    <Button
                      size='small'
                      sx={{ marginBottom: '3px', color: 'white' }}
                      startIcon={<RestoreOutlined />}
                      onClick={() => {
                        if (setOpenSettlementHistory) {
                          setOpenSettlementHistory(true);
                        }
                      }}>
                      View History
                    </Button>
                  </Grid>
                )}
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid
                className={isOrderInactive(order?.Status) ? 'cancel-style' : ''}
                item>
                {order &&
                  order.OrderDetails &&
                  order?.OrderDetails?.SettlementTeam && (
                    <OrderDetailsTable
                      columns={settlementTeamColumns}
                      data={getSettlementTeamTableData()}
                      noDataText={`${
                        order?.OrderType?.DocGen === DocGenerationSource.CX
                          ? 'Settlement'
                          : 'Escrow'
                      } Team not yet selected`}
                    />
                  )}
              </Grid>
            </AccordionDetails>
          </Accordion>
          {order.OrderType.Type === OrderTypes.IPEN && (
            <Accordion
              expanded={notaryPanel}
              onChange={() => {
                if (setNotaryPanel) {
                  setNotaryPanel(!notaryPanel);
                }
              }}
              sx={{ marginTop: 1, marginBottom: 1 }}>
              <AccordionSummary aria-controls='notary-content' id='notary'>
                <Typography>NOTARY</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid
                  className={
                    isOrderInactive(order?.Status) ? 'cancel-style' : ''
                  }
                  item>
                  <ConformXNotaryDetails
                    notaryStatusOnOrder={
                      Status[order?.OrderDetails?.IPEN?.Notary?.Status]
                    }
                    data={{ ...order }}
                    tableStyle={{ minWidth: 650 }}
                    ipenSession={ipenSession}
                    getSsoToken={getSsoToken}
                  />
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
          <Accordion
            expanded={downloadPanel}
            ref={documentRef}
            onChange={() => setDownloadPanel(!downloadPanel)}
            sx={{ marginTop: 1, marginBottom: 1, zIndex: documentZIndex }}>
            <AccordionSummary aria-controls='download-content' id='download'>
              <Typography>DOCUMENT CENTER</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {order.OrderType.Type === OrderTypes.IPEN && (
                <DocumentsDownloaderUploader
                  order={order}
                  documentsSelected={documentsSelected}
                  setDocumentsSelected={setDocumentsSelected}
                  getDocuments={goGetDocuments}
                  goGetDocumentsType={goGetDocumentsType}
                />
              )}
              {order.OrderType.Type === OrderTypes.HybridEClose && (
                <DocumentsDownloaderUploader
                  order={order}
                  documentsSelected={documentsSelected}
                  setDocumentsSelected={setDocumentsSelected}
                  getDocuments={goGetDocuments}
                  goGetDocumentsType={goGetDocumentsType}
                  setPrintAllInkSignDocuments={goPrintAllInkSignDocuments}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </>
  );
}

export default OrderDetailsAccordion;
